import { ReactNode, useEffect, useMemo } from 'react';

// material-ui
import { createTheme, ThemeOptions, ThemeProvider, Theme, TypographyVariantsOptions, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// project-imports
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';
import GlobalStyles from './GlobalStyles';

import { HEADER_HEIGHT, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import { useThemeScope } from 'contexts/ThemeScopeContext';
import getWindowScheme from 'utils/getWindowScheme';
import { clacUiGlobal as ui } from 'utils/uiGlobal';

// types
import { CustomShadowProps } from 'types/theme';

type ThemeCustomizationProps = {
  children: ReactNode;
};

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({ children }: ThemeCustomizationProps) {
  const { themeDirection, mode, presetColor, fontFamily, themeContrast } = useConfig();
  // Una rama del árbol puede fijar el modo (login, activación, flujo del tercero).
  const { forcedMode } = useThemeScope();
  let themeMode = forcedMode ?? mode;
  if (themeMode === ThemeMode.AUTO) {
    const autoMode = getWindowScheme();
    if (autoMode) {
      themeMode = ThemeMode.DARK;
    } else {
      themeMode = ThemeMode.LIGHT;
    }
  }

  // Punto único donde se conmutan las variables CSS de `uiTokens.ts`. Todo lo que use
  // tokens de `uiGlobal` cambia de modo con esto, sin volver a renderizar.
  useEffect(() => {
    document.documentElement.dataset.clacMode = themeMode;
  }, [themeMode]);

  const theme: Theme = useMemo<Theme>(() => Palette(themeMode, presetColor, themeContrast), [themeMode, presetColor, themeContrast]);

  const themeTypography: TypographyVariantsOptions = useMemo<TypographyVariantsOptions>(
    () => Typography(fontFamily),

    [fontFamily]
  );
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => CustomShadows(theme), [theme]);

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: HEADER_HEIGHT,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      palette: theme.palette,
      shape: {
        borderRadius: Number.parseInt(ui.radius.sm, 10)
      },
      customShadows: themeCustomShadows,
      typography: themeTypography
    }),
    [themeDirection, theme, themeTypography, themeCustomShadows]
  );

  // Memoizado a propósito: `createTheme` + los 52 archivos de `componentsOverride` se
  // reconstruían en cada render de este provider, que envuelve toda la app. Eso cambiaba la
  // identidad del tema en cada render y forzaba a MUI a recalcular los estilos de todo el
  // árbol — se notaba como un tirón de varios segundos al cambiar de modo claro/oscuro.
  const themes: Theme = useMemo(() => {
    const created = createTheme(themeOptions);
    created.components = componentsOverride(created);
    return created;
  }, [themeOptions]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline enableColorScheme />
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
